import { BLOCK_ALIGN_TYPES } from "@corca/tiptap";
import { findParentNodeClosestToPos } from "@tiptap/core";

import { SLASH_COMMAND_ALIGN_TYPES } from "@/components/suggestions/types";

import { filterSlashCommandItems, SLASH_COMMAND_ITEMS } from "./data/slashCommandItems";

import type { SlashCommandItem, SlashCommandSuggestionItem } from "./data/slashCommandItems";
import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";

const ALIGN_TYPES = new Set<string>(SLASH_COMMAND_ALIGN_TYPES);

// Alignable nodes plus the table frame that holds alignable cells; anything else (list, quote,
// callout, future containers) hides align, since aligning a single wrapped line is unintended.
const ALIGN_ALLOWED_ANCESTORS = new Set<string>([...BLOCK_ALIGN_TYPES, "table", "tableRow"]);

const SLASH_PARENT_TYPES = new Set([
  "paragraph",
  "heading",
  "blockquote",
  "callout",
  "listItem",
  "taskItem",
]);

function canAlignCurrentBlock(doc: DocumentModel): boolean {
  const { tiptap } = doc.editor;
  if (!tiptap) return false;
  const forbidden = findParentNodeClosestToPos(
    tiptap.state.selection.$from,
    (node) => !ALIGN_ALLOWED_ANCESTORS.has(node.type.name),
  );
  return !forbidden;
}

const SLASH_PRIORITY = 1000;

export function isSlashTriggerValid(doc: DocumentModel): boolean {
  const { tiptap } = doc.editor;
  if (!tiptap) return false;

  if (doc.cursor.isActive || doc.editor.editorState.isExpression) return false;

  const { $from } = tiptap.state.selection;
  const parentName = $from.parent.type.name;
  if (parentName === "codeBlock") return false;
  if (!SLASH_PARENT_TYPES.has(parentName)) return false;

  const { parentOffset } = $from;
  if (parentOffset === 0) return false;

  const text = $from.parent.textBetween(0, parentOffset, "\n", "\0");

  for (let i = text.length - 1; i >= 0; i -= 1) {
    const ch = text[i];
    if (/\s/.test(ch)) return false;
    if (ch === "/") {
      const before = i === 0 ? "" : text[i - 1];
      return before === "" || /\s/.test(before);
    }
  }
  return false;
}

function toSuggestion(
  item: SlashCommandItem,
  index: number,
): SingleSuggestion<"slashCommand"> & { item: SlashCommandSuggestionItem } {
  return {
    type: "single",
    item: {
      type: "slashCommand",
      symbol: item.label,
      intlName: item.intlName,
      kind: item.kind,
      category: item.subtitle,
    },
    icon: item.icon,
    forceIcon: true,
    priority: SLASH_PRIORITY + (SLASH_COMMAND_ITEMS.length - index),
  };
}

/** True when the slash menu owns the "/" input, so "/"-keyed operator providers step aside. */
export function isSlashMenuActive(str: string, doc: DocumentModel): boolean {
  if (!str.startsWith("/")) return false;
  return isSlashTriggerValid(doc);
}

export const slashCommandSuggestionsProvider: SuggestionProvider<"slashCommand", "slashCommand"> = {
  type: "slashCommand",
  search: (str, doc) => {
    if (!isSlashMenuActive(str, doc)) return [];

    const query = str.slice(1);
    let items = filterSlashCommandItems(query);

    if (!canAlignCurrentBlock(doc)) {
      items = items.filter((item) => !ALIGN_TYPES.has(item.kind));
    }

    return [items.map(toSuggestion)];
  },
};
